import React from 'react';
import { Chip } from '@mui/material';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import FenceOutlinedIcon from '@mui/icons-material/FenceOutlined';
import RealEstateAgentOutlinedIcon from '@mui/icons-material/RealEstateAgentOutlined';
import { usePreviewHighlights } from '../hooks';
import { formatPrice } from '../utils';
import PreviewTitle from './PreviewTitle';
import StatsGrid from './StatsGrid';

/**
 * Modernized Highlights section using StatsGrid and PreviewTitle.
 * Replaces the old HighlightsSection with a responsive grid layout.
 */
const HighlightsGrid: React.FC = () => {
  const {
    aboveGrade,
    addressLine1,
    addressLine2,
    baths,
    beds,
    halfBaths,
    listPrice,
    lotSizeAcres,
    lotSizeSqft,
    mlsStatus,
    propertyType,
    sqft,
  } = usePreviewHighlights();

  const displaySqft = aboveGrade || sqft;
  const totalBaths = (Number(baths) || 0) + (Number(halfBaths) || 0) / 2;
  const lotSize =
    (lotSizeAcres && `${Number(lotSizeAcres).toLocaleString()} Acres`) ||
    (lotSizeSqft && `${Number(lotSizeSqft).toLocaleString()} Sq Ft`);

  // Receiving an object of Stats as requested
  const stats = {
    beds: {
      icon: <BedOutlinedIcon />,
      value: beds || '0',
      label: 'Bedrooms',
      // show: !!beds,
    },
    baths: {
      icon: <BathtubOutlinedIcon />,
      value: `${totalBaths} (${baths || '0'}F / ${halfBaths || '0'}H)`,
      label: 'Bathrooms',
      // show: !!baths || !!halfBaths,
    },
    sqft: {
      icon: <HomeOutlinedIcon />,
      value: `${displaySqft ? Number(displaySqft).toLocaleString() : '0'} Sq Ft`,
      label: 'Home Size',
      show: !!displaySqft,
    },
    lotSizeSqFt: {
      icon: <FenceOutlinedIcon />,
      value: lotSize,
      label: 'Lot Size',
      show: !!lotSize,
    },
    propertyType: {
      icon: <RealEstateAgentOutlinedIcon />,
      value: propertyType || 'N/A',
      label: 'Property Type',
      show: !!propertyType && !lotSize,
    },
  };

  const status = mlsStatus?.toLowerCase().replace('-bright', '');

  return (
    <>
      <PreviewTitle
        addressLine1={addressLine1 || 'Address Unavailable'}
        addressLine2={addressLine2}
        price={formatPrice(listPrice)}
        badge={
          status ? (
            <Chip
              label={status}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: '20px',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            />
          ) : undefined
        }
      />
      <StatsGrid stats={stats} />
    </>
  );
};

export default React.memo(HighlightsGrid);
